// SF2 — from "ACOS is high" to the search term causing it.
//
// Tabs, not a tree: a large account has tens of thousands of search terms, and
// a tree of them is unusable. Each level opens the next filtered to what was
// clicked, so three clicks get from the account to the term.
import React from 'react';
import s from '../AdsAnalytics.module.css';
import { money, num, ratio, LABEL_TONE } from '../format';

const LEVELS = [
  ['campaigns', 'Campaigns'], ['ad_groups', 'Ad groups'],
  ['targets', 'Keywords & targets'], ['search_terms', 'Search terms'],
];
const FILTERS = [
  ['', 'All'], ['Waste', 'Waste'], ['High ACOS', 'High ACOS'], ['Healthy', 'Healthy'],
  ['no_impressions', 'No impressions'],
];

export default function Drill({ data, loading, level, parent, filter,
                               onLevel, onFilter, onOpen, onBack }) {
  if (loading && !data) return <div className={s.empty}>Loading…</div>;
  if (!data) return null;
  const currency = data.currency;

  return (
    <>
      <div className={s.chipRow}>
        {LEVELS.map(([id, label]) => (
          <button key={id} className={level === id ? s.chipActive : s.chip}
                  onClick={() => onLevel(id)}>{label}</button>
        ))}
        {parent && (
          <button className={s.clearFilter} onClick={onBack}>
            Clear the filter on {parent}
          </button>
        )}
      </div>

      <div className={s.chipRow} style={{ marginTop: 8 }}>
        {FILTERS.map(([id, label]) => (
          <button key={id || 'all'} className={filter === id ? s.chipActive : s.chip}
                  onClick={() => onFilter(id)}>{label}</button>
        ))}
      </div>

      {data.match_types && (
        <div className={s.matchRow}>
          {data.match_types.types.map((m) => (
            <span key={m.match_type} className={s.matchChip}>
              <strong>{m.match_type}</strong> {money(m.spend, currency)} ·{' '}
              {ratio(m.acos, m.acos_reason)}
            </span>
          ))}
          <span className={s.hint}>{data.match_types.note}</span>
        </div>
      )}

      {!data.rows?.length ? (
        <div className={s.empty}>Nothing at this level for the filter chosen.</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{LEVELS.find(([id]) => id === level)?.[1]}</th>
                <th className={s.right}>Spend</th>
                <th className={s.right}>Ad sales</th>
                <th className={s.right}>ACOS</th>
                <th className={s.right}>Orders</th>
                <th className={s.right}>Clicks</th>
                <th className={s.right}>CPC</th>
                {level === 'targets' && <th className={s.right}>Bid</th>}
                <th>Verdict</th>
                <th className={s.right}>Waste</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className={r.no_activity ? s.rowMuted : undefined}>
                  <td>
                    {level === 'search_terms' || !onOpen ? (
                      <span className={s.term}>{r.name}</span>
                    ) : (
                      <button className={s.link} onClick={() => onOpen(r.id)}>{r.name}</button>
                    )}
                    {r.match_type && <span className={s.tag}>{r.match_type}</span>}
                    {r.already_exact && <span className={s.tag}>already exact</span>}
                    {r.high_performer && <span className={s.tagGood}>high performer</span>}
                    {r.bid_limited && <span className={s.tag}>bid-limited</span>}
                  </td>
                  <td className={s.right}>{money(r.spend, currency)}</td>
                  <td className={s.right}>{money(r.ad_sales, currency)}</td>
                  <td className={s.right}>{ratio(r.acos, r.acos_reason)}</td>
                  <td className={s.right}>{num(r.orders)}</td>
                  <td className={s.right}>{num(r.clicks)}</td>
                  <td className={s.right}>{r.cpc == null ? '—' : money(r.cpc, currency)}</td>
                  {level === 'targets' && (
                    <td className={s.right}>{r.bid == null ? '—' : money(r.bid, currency)}</td>
                  )}
                  <td>
                    <span className={`${s.verdict} ${s[LABEL_TONE[r.label] || 'muted']}`}
                          title={r.why}>{r.label}</span>
                  </td>
                  <td className={s.right}>{r.waste ? money(r.waste, currency) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.note && <p className={s.hint} style={{ marginTop: 10 }}>{data.note}</p>}
      <p className={s.hint}>
        Campaign totals come from the campaign report, never from adding the search terms
        under them. {data.attribution}
      </p>
    </>
  );
}
